import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Project, ApprovalStatus } from '../services/api';

// ── Helpers ───────────────────────────────────────────────────

function getApprovalBadge(status: ApprovalStatus) {
  const map: Record<string, { label: string; cls: string }> = {
    'Draft':             { label: 'Draft',        cls: 'badge badge-neutral' },
    'Director Review':   { label: 'Dir Review',   cls: 'badge badge-warning' },
    'Director Approved': { label: 'Dir ✓',        cls: 'badge badge-success' },
    'Director Denied':   { label: 'Dir Denied',   cls: 'badge badge-danger'  },
    'IT Review':         { label: 'IT Review',    cls: 'badge badge-warning' },
    'IT Approved':       { label: 'IT ✓',         cls: 'badge badge-success' },
    'IT Denied':         { label: 'IT Denied',    cls: 'badge badge-danger'  },
    'EMT Review':        { label: 'EMT Review',   cls: 'badge badge-warning' },
    'AGM Review':        { label: 'EMT Review',   cls: 'badge badge-warning' }, // legacy alias
    'EMT Denied':        { label: 'EMT Denied',   cls: 'badge badge-danger'  },
    'AGM Denied':        { label: 'EMT Denied',   cls: 'badge badge-danger'  }, // legacy alias
    'CEO Review':        { label: 'CEO Review',   cls: 'badge badge-warning' },
    'Revision':          { label: 'Revision',     cls: 'badge badge-warning' },
    'Paused':            { label: 'Paused',       cls: 'badge badge-neutral' },
    'Approved':          { label: 'Approved',     cls: 'badge badge-success' },
    'Denied':            { label: 'Denied',       cls: 'badge badge-danger'  },
  };
  const b = map[status] || { label: status, cls: 'badge badge-neutral' };
  return <span className={b.cls}>{b.label}</span>;
}

function PriorityDot({ priority }: { priority: string }) {
  const cls = priority === 'High' || priority === 'Critical' ? 'p-high' : priority === 'Low' ? 'p-low' : 'p-normal';
  return <span className={`priority-dot ${cls}`} />;
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(n: number | null) {
  if (!n) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0 });
}

// ── Types ─────────────────────────────────────────────────────

type SortField = 'Title' | 'DepartmentName' | 'Priority' | 'ApprovalStatus' | 'EndDate' | 'AmountBudgeted';
type SortDir   = 'asc' | 'desc';

const PRIORITY_ORDER: Record<string, number> = { Critical: 0, High: 1, Normal: 2, Medium: 2, Low: 3, TBD: 4 };
const STATUS_ORDER: Record<string, number> = {
  'Director Review': 0, 'EMT Review': 1, 'AGM Review': 1, 'IT Review': 2, 'CEO Review': 3,
  'Revision': 4, 'Draft': 5,
  'Director Approved': 6, 'IT Approved': 7, 'Approved': 8, 'Paused': 9,
  'Director Denied': 10, 'EMT Denied': 10, 'AGM Denied': 10, 'IT Denied': 11, 'Denied': 12,
};

// ── Page ──────────────────────────────────────────────────────

export function ProjectsPage() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const [search, setSearch]               = useState('');
  const [deptFilter, setDeptFilter]       = useState('All');
  const [statusFilter, setStatusFilter]   = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');

  const [sortField, setSortField] = useState<SortField>('EndDate');
  const [sortDir, setSortDir]     = useState<SortDir>('asc');

  useEffect(() => {
    api.projects.list()
      .then(data => setProjects(data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const departments = ['All', ...Array.from(new Set(projects.map(p => p.DepartmentName))).sort()];

  // EMT replaces AGM in status filter options
  const statusGroups = [
    'All', 'Director Review', 'EMT Review', 'IT Review', 'CEO Review',
    'Revision', 'Paused', 'Approved', 'IT Denied', 'Director Denied', 'EMT Denied', 'Draft',
  ];

  const filtered = projects
    .filter(p => {
      if (p.IsDraft) return false; // hide drafts from main list
      const q = search.toLowerCase();
      if (search && ![p.Title, p.RequestorName, p.DepartmentName, p.ProjectManagerName || '']
        .some(s => s.toLowerCase().includes(q))) return false;
      if (deptFilter !== 'All'     && p.DepartmentName  !== deptFilter)     return false;
      // Handle AGM Review as EMT Review alias
      if (statusFilter !== 'All') {
        const normalizedStatus = (p.ApprovalStatus === 'AGM Review' ? 'EMT Review' : p.ApprovalStatus);
        if (normalizedStatus !== statusFilter) return false;
      }
      if (priorityFilter !== 'All' && p.Priority !== priorityFilter) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'Priority') {
        cmp = (PRIORITY_ORDER[a.Priority] ?? 9) - (PRIORITY_ORDER[b.Priority] ?? 9);
      } else if (sortField === 'ApprovalStatus') {
        cmp = (STATUS_ORDER[a.ApprovalStatus] ?? 9) - (STATUS_ORDER[b.ApprovalStatus] ?? 9);
      } else if (sortField === 'AmountBudgeted') {
        cmp = (a.AmountBudgeted || 0) - (b.AmountBudgeted || 0);
      } else if (sortField === 'EndDate') {
        const aDate = a.EndDate ? new Date(a.EndDate).getTime() : 0;
        const bDate = b.EndDate ? new Date(b.EndDate).getTime() : 0;
        cmp = aDate - bDate;
      } else {
        cmp = ((a[sortField] as string) || '').localeCompare((b[sortField] as string) || '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <span style={{ color: 'var(--text3)', marginLeft: 4 }}>↕</span>;
    return <span style={{ color: 'var(--accent)', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const activeFilters = [deptFilter, statusFilter, priorityFilter].filter(f => f !== 'All').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">All Projects</h1>
          <p className="page-sub">
            {loading ? 'Loading...' : `${filtered.length} of ${projects.filter(p => !p.IsDraft).length} projects`}
            {activeFilters > 0 && ` · ${activeFilters} filter${activeFilters > 1 ? 's' : ''} active`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/new')}>+ New Project Request</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: 200, maxWidth: 320 }}>
          <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}
            width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="6" cy="6" r="4"/><path d="M9.5 9.5l3 3"/>
          </svg>
          <input className="field-input" style={{ paddingLeft: 30, fontSize: 13 }} placeholder="Search projects, requestor..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="field-select" style={{ width: 'auto', fontSize: 13 }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
          {departments.map(d => <option key={d}>{d}</option>)}
        </select>
        <select className="field-select" style={{ width: 'auto', fontSize: 13 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {statusGroups.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="field-select" style={{ width: 'auto', fontSize: 13 }} value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
          {['All', 'Critical', 'High', 'Normal', 'Low', 'TBD'].map(p => <option key={p}>{p}</option>)}
        </select>
        {activeFilters > 0 && (
          <button className="btn" style={{ fontSize: 12 }} onClick={() => { setDeptFilter('All'); setStatusFilter('All'); setPriorityFilter('All'); setSearch(''); }}>
            ✕ Clear filters
          </button>
        )}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {error && (
          <div style={{ padding: 24, color: 'var(--danger-text)', background: 'var(--danger-bg)', margin: 16, borderRadius: 8 }}>
            Failed to load projects: {error}
          </div>
        )}
        {loading && <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>Loading projects...</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state" style={{ padding: 48 }}>
            <div className="empty-state-icon">🔍</div>
            <div className="empty-state-title">No projects match your filters</div>
            <div className="empty-state-sub">Try adjusting your search or filter criteria.</div>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('Title')} style={{ cursor: 'pointer', userSelect: 'none' }}>Project <SortIcon field="Title" /></th>
                <th onClick={() => toggleSort('DepartmentName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Department <SortIcon field="DepartmentName" /></th>
                <th onClick={() => toggleSort('Priority')} style={{ cursor: 'pointer', userSelect: 'none' }}>Priority <SortIcon field="Priority" /></th>
                <th onClick={() => toggleSort('ApprovalStatus')} style={{ cursor: 'pointer', userSelect: 'none' }}>Status <SortIcon field="ApprovalStatus" /></th>
                <th onClick={() => toggleSort('AmountBudgeted')} style={{ cursor: 'pointer', userSelect: 'none' }}>Budget <SortIcon field="AmountBudgeted" /></th>
                <th onClick={() => toggleSort('EndDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Target Date <SortIcon field="EndDate" /></th>
                <th>Project Manager</th>
                <th>Director</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.ProjectID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${p.ProjectID}`)}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{p.Title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>{p.RequestorName}</div>
                  </td>
                  <td style={{ color: 'var(--text2)', fontSize: 13 }}>{p.DepartmentName}</td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PriorityDot priority={p.Priority} />{p.Priority}
                    </span>
                  </td>
                  <td>{getApprovalBadge(p.ApprovalStatus)}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{formatCurrency(p.AmountBudgeted)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{formatDate(p.EndDate)}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{p.ProjectManagerName || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{p.DirectorName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
